import {
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import DrawerComponent from '../../../../../components/Drawer/Drawer';
import CloseIcon from '@mui/icons-material/Close';
import { TOAST_TYPES } from '@constants/constants';
import { DeleteOutline } from '@mui/icons-material';
import { useLazyGetCategoriesAndGenresQuery, useGetGamesPaginatedInfiniteQuery } from '@api/games';
import { setToastMessage } from '@stores/App/slice';
import { useDispatch, useSelector } from 'react-redux';
import useCompany from '@hooks/api/useCompany';
import { RootState } from '@stores/store';
import { CompanyListState } from '@stores/CompanyList/types/companyList';
import CategorySelector from './CategorySelector';
import GenreSelector from './GenreSelector';
import { constructFetchGamesQueryString } from '@utils/common';
import { useCreatePortfolioMutation } from '@api/selfServe';
import { InfiniteSearchSelect, SelectWithLabel } from '@components';

const initialGamesState = [
  { gameId: '', gameName: '', dealName: '' },
  { gameId: '', gameName: '', dealName: '' },
];

interface NewPortfolioSidePanelProps {
  showNewPortfolioSidePanel: boolean;
  setShowNewPortfolioSidePanel: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateSuccess: () => void;
}

const NewPortfolioSidePanel: React.FC<NewPortfolioSidePanelProps> = ({
  showNewPortfolioSidePanel,
  setShowNewPortfolioSidePanel,
  onCreateSuccess,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [gamesList, setGamesList] = useState<any[]>(initialGamesState);
  const [newPortfolioData, setNewPortfolioData] = useState<any>({
    dealName: '',
    email: '',
    category: '',
    genre: '',
    companyName: '',
    clientCode: '',
    companyId: '',
    gameIds: [],
  });
  const [categoryOptions, setCategoryOptions] = useState<{ name: string; genres: string[] }[]>([]);
  const [genreOptions, setGenreOptions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchCategoryAndGenreOptionsApi] = useLazyGetCategoriesAndGenresQuery();
  const { getCompanyList } = useCompany();

  const queryString = useMemo(() => {
    return constructFetchGamesQueryString({
      pageSize: 20,
      search: searchTerm,
    });
  }, [searchTerm]);

  const {
    data: gameList,
    isLoading: isLoadingGames,
    isError,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useGetGamesPaginatedInfiniteQuery(queryString);

  const [createPortfolio, { isLoading }] = useCreatePortfolioMutation();

  const companiesList: CompanyListState = useSelector((state: RootState) => state?.CompanyListData);

  const fetchCategoryAndGenreOptions = async (): Promise<void> => {
    try {
      const response = await fetchCategoryAndGenreOptionsApi().unwrap();
      const { data } = response;

      setCategoryOptions(data.categories);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  const handleUpdateNewPortfolioData = (key: string, value: string) => {
    let updatedNewPortfolio = { ...newPortfolioData };
    if (key === 'category') {
      updatedNewPortfolio.genre = '';
    }

    updatedNewPortfolio = {
      ...updatedNewPortfolio,
      [key]: value,
    };

    if (key === 'clientCode') {
      const selectedCompany = (companiesList.companyList ?? []).find(
        (company) => company.clientCode === value,
      );
      if (selectedCompany) {
        updatedNewPortfolio.companyId = selectedCompany.id;
        updatedNewPortfolio.companyName = selectedCompany.name;
      }
    }

    setNewPortfolioData(updatedNewPortfolio);
  };

  const handleCreateNewPortfolio = async () => {
    const selectedCompany = companiesList.companyList.find(
      (company) => company.name === newPortfolioData.companyName,
    );
    const gameIds = gamesList.filter((game) => game.gameId).map((game) => game.gameId);

    const payload = {
      dealName: newPortfolioData.dealName,
      email: newPortfolioData.email,
      category: newPortfolioData.category,
      genre: newPortfolioData.genre,
      clientCode: selectedCompany?.clientCode,
      companyId: selectedCompany?.id,
      gameIds: gameIds,
    };

    try {
      await createPortfolio(payload).unwrap();
      if (onCreateSuccess) {
        onCreateSuccess();
      }
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || 'Failed to create portfolio',
        }),
      );
    }
  };

  // Hook for fetching the category and genre options.
  useEffect(() => {
    fetchCategoryAndGenreOptions();
    getCompanyList();
  }, []);

  useEffect(() => {
    if (!newPortfolioData?.category) return;

    let genres: string[] = [];
    for (const cat of categoryOptions) {
      if (cat.name !== newPortfolioData.category) continue;

      genres = cat.genres;
      break;
    }

    setGenreOptions(genres);
  }, [newPortfolioData?.category]);

  const isFormValid =
    newPortfolioData.dealName &&
    newPortfolioData.email &&
    newPortfolioData.category &&
    newPortfolioData.genre &&
    newPortfolioData.companyName &&
    gamesList.filter((game) => game.gameId).length >= 2;

  const gameIdOptions = useMemo(() => {
    return (
      gameList?.pages
        .flatMap((page) => page.data.items)
        .map((game) => ({
          label: game.gameDetails.gameId,
          value: game.gameDetails.gameId,
        })) || []
    );
  }, [gameList]);

  return (
    <DrawerComponent
      open={showNewPortfolioSidePanel}
      anchor="right"
      close={() => {
        setShowNewPortfolioSidePanel(false);
      }}
      height="98vh"
      zIndex={1300}
      backgroundColor="#F0F0F1"
    >
      <Box
        sx={{
          width: '900px',
          maxWidth: isMobile ? 'min(900px, 95vw)' : '900px',
          height: '100%',
          padding: '1.5rem',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '1.375rem',
            fontWeight: 700,
            lineHeight: '1.875rem',
            color: '#6C6C6C',
            marginBottom: '1.5rem',
          }}
        >
          New Portfolio
          <Button
            sx={{ padding: 0, minWidth: 'fit-content' }}
            onClick={() => {
              setShowNewPortfolioSidePanel(false);
            }}
          >
            <CloseIcon sx={{ color: '#162C36' }} />
          </Button>
        </Box>
        <Stack gap={'1.5rem'}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              width: '100%',
              backgroundColor: '#FFFFFF',
              padding: '1.5rem',
              borderRadius: '1rem',
            }}
          >
            <TextField
              label="Deal Name"
              sx={{
                width: '100%',
              }}
              value={newPortfolioData.dealName}
              onChange={(e: any) => handleUpdateNewPortfolioData('dealName', e.target.value)}
              required
            />
            <TextField
              label="Email ID"
              sx={{
                width: '100%',
              }}
              value={newPortfolioData.email}
              onChange={(e: any) => handleUpdateNewPortfolioData('email', e.target.value)}
              required
            />
            <CategorySelector
              categories={categoryOptions.map((option) => option.name)}
              selectedCategory={newPortfolioData.category}
              onCategoryChange={(category) => handleUpdateNewPortfolioData('category', category)}
            />
            <GenreSelector
              genres={genreOptions}
              selectedGenre={newPortfolioData.genre}
              onGenreChange={(genre: string) => handleUpdateNewPortfolioData('genre', genre)}
            />
            <SelectWithLabel
              label="Company Name"
              field="clientCode"
              required
              labelId="company-select-label"
              menuItems={(companiesList.companyList ?? []).map((company) => ({
                label: company.name,
                value: company.clientCode || '',
              }))}
              onChange={(value) => {
                handleUpdateNewPortfolioData(
                  'clientCode',
                  typeof value === 'string' ? value : (value.value ?? ''),
                );
              }}
              isAddNew={true}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
              value={newPortfolioData.clientCode}
              fullWidth
              selectStyles={{
                height: '56px',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                borderRadius: '0.25rem',
                paddingRight: '0.5rem',
              }}
            />
          </Box>
          {/* Game Selection */}
          <Box
            sx={{
              width: '100%',
              backgroundColor: '#FFFFFF',
              padding: '1.5rem',
              borderRadius: '1rem',
            }}
          >
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              lineHeight={'1.375rem'}
              color={'#6C6C6C'}
              marginBottom={'1.25rem'}
            >
              Select Apps
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                justifyContent: 'flex-start',
                width: '100%',
              }}
            >
              {gamesList.map((game: any, index: number) => (
                <Box
                  key={`${index}--${game.gameId}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: '3rem',
                    paddingY: '0.25rem',
                  }}
                >
                  <InfiniteSearchSelect
                    key={`${index}--${game.gameId}`}
                    placeholder="Select Game ID"
                    options={gameIdOptions}
                    hasNextPage={hasNextPage}
                    isFetching={isFetching}
                    fetchNextPage={fetchNextPage}
                    isLoading={isLoadingGames}
                    isError={isError}
                    errorText="Error loading games"
                    noOptionsText="No games found"
                    selectStyles={{
                      width: '50%',
                    }}
                    onSearchChange={(value) => {
                      setSearchTerm(value);
                    }}
                    selectedValue={game.gameId}
                    onSelect={(game) => {
                      const selectedGame = gameList?.pages
                        .flatMap((page) => page.data.items)
                        .find((opt) => opt.gameDetails.gameId === game.value);
                      const updatedGamesList = [...gamesList];
                      updatedGamesList[index] = {
                        ...updatedGamesList[index],
                        gameId: selectedGame?.gameDetails.gameId,
                        gameName: selectedGame?.gameDetails.name,
                        dealName: selectedGame?.gameDetails.dealName,
                      };
                      setGamesList(updatedGamesList);
                      setSearchTerm('');
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '0.5rem',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      width: '50%',
                    }}
                  >
                    <Box sx={{ width: '100%', flex: 1 }}>
                      <Typography
                        fontSize={'0.75rem'}
                        fontWeight={500}
                        lineHeight={'1rem'}
                        color={'#6C6C6C'}
                        paddingBottom={'0.25rem'}
                      >
                        Game Name
                      </Typography>
                      <Typography
                        fontSize={'0.875rem'}
                        fontWeight={700}
                        lineHeight={'1.25rem'}
                        color={'#162936'}
                        letterSpacing={'-0.35px'}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {game?.gameName ? game.gameName : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', flex: 1 }}>
                      <Typography
                        fontSize={'0.75rem'}
                        fontWeight={500}
                        lineHeight={'1rem'}
                        color={'#6C6C6C'}
                        paddingBottom={'0.25rem'}
                      >
                        Deal Name
                      </Typography>
                      <Typography
                        fontSize={'0.875rem'}
                        fontWeight={700}
                        lineHeight={'1.25rem'}
                        color={'#162936'}
                        letterSpacing={'-0.35px'}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {game?.dealName ? game.dealName : '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    sx={{
                      minWidth: 'fit-content',
                      ':disabled': { opacity: 0.5 },
                    }}
                    disabled={gamesList.length <= 2}
                    onClick={() => {
                      setGamesList(gamesList.filter((_, i) => i !== index));
                    }}
                  >
                    <DeleteOutline sx={{ color: '#2F736E' }} />
                  </Button>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                setGamesList([...gamesList, { gameId: '', gameName: '', dealName: '' }]);
              }}
              disabled={gamesList.some((game) => !game.gameId)}
              sx={{
                borderRadius: '100px',
                fontWeight: '700',
                textTransform: 'none',
                backgroundColor: 'transparent',
                border: gamesList.some((game) => !game.gameId)
                  ? '1px solid #E0E0E0'
                  : '1px solid #2E4A4F',
                color: '#6C6C6C',
                fontFamily: 'Manrope, sans-serif',
                '&:hover': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
                padding: '10px 24px',
                boxShadow: 'none',
                marginTop: '1.5rem',
              }}
            >
              Add More
            </Button>
          </Box>
        </Stack>
      </Box>
      <Box
        sx={{
          width: isMobile ? 'auto' : '500px',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          bottom: '0',
          borderTop: '1px solid #E8EAEB',
          backgroundColor: '#f0f0f1',
        }}
      >
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          variant="contained"
          onClick={handleCreateNewPortfolio}
          disabled={isLoading || !isFormValid}
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            background: '#2F736E',
            color: '#fff',
          }}
        >
          {isLoading && <CircularProgress size={20} sx={{ marginRight: '0.5rem' }} />}
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={() => setShowNewPortfolioSidePanel(false)}
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            background: 'none',
            color: '#51B8B0',
            ':hover': {
              background: 'none',
            },
          }}
        >
          Close
        </Button>
      </Box>
    </DrawerComponent>
  );
};

export default NewPortfolioSidePanel;
